import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Button,
  CircularProgress,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { styled } from "@mui/material/styles";
import { TypographyNormal } from "../customComponent";
import AssignedComponentItem from "../assign/AssignedComponentItem";
import { useDebounce } from "../hooks";
import { colorStyling } from "../../helpers/color";
import { likeSearchInArray } from "../../helpers/arrayHelper";
import { logger } from "../../helpers/logger";

const PREFIX = 'ScheduleListDialog';

const classes = {
  hover: `${PREFIX}-hover`,
  hoverPaper: `${PREFIX}-hoverPaper`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const StyledBox = styled('div')(() => ({
  [`& .${classes.hover}`]: {
    "&:hover": {
      cursor: "pointer",
      textDecoration: "underline",
      color: colorStyling.primary,
    },
  },

  [`& .${classes.hoverPaper}`]: {
    "&:hover": { backgroundColor: colorStyling.white.hover },
  }
}));

const InputSearch = styled("input")(() => ({
  outline: "none",
  border: "none",
  fontSize: "16px",
  lineHeight: "26px",
  fontWeight: 400,
  width: "100%",
  marginLeft: "10px",
  fontFamily: "Inter, sans-serif",
}));

const BoxInput = styled(Box)(({ theme }) => ({
  border: `1px solid ${colorStyling.white.border}`,
  display: "flex",
  alignItems: "center",
  paddingLeft: theme.spacing(1.875),
  paddingRight: theme.spacing(1.875),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  borderRadius: "6px",
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const ScheduleListDialog = ({ group, open, handleClose }) => {
  const { t } = useTranslation("group");
  const { t: tCommon } = useTranslation();

  const { user } = useSelector((state) => state.auth);
  const permission = user?.permission;
  const schedulePermission = permission?.schedule;

  const [loading, setLoading] = useState(false);
  const [listSchedule, setListSchedule] = useState([]);

  useEffect(() => {
    if (open) {
      setListSchedule(group.schedules);
    } else {
      setSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group.schedules]);

  /**
   * 1 Second delay after input value change to do API fetch
   */
  const [search, setSearch] = useState("");
  const debouncedSearchTerm = useDebounce(search, 1000);
  useEffect(
    () => {
      if (debouncedSearchTerm.trim().length === 0) {
        if (listSchedule.length !== group.schedules.length) {
          setListSchedule(group.schedules);
        }
        return;
      }

      try {
        setLoading(true);
        const results = likeSearchInArray(
          group.schedules,
          "name",
          debouncedSearchTerm
        );

        setListSchedule(results);
        setLoading(false);
      } catch (error) {
        logger.error(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearchTerm] // Only call effect if debounced search term changes
  );

  return (
    <>
      <Dialog maxWidth="sm" open={open} onClose={handleClose} fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {t("scheduleList.title")}
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent>
          <BoxInput>
            <SearchIcon
              fontSize="medium"
              sx={{ color: colorStyling.gray.normal }}
            />

            <InputSearch
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </BoxInput>

          <Box
            sx={{
              mt: 1,
              maxHeight: 300,
              overflowY: "auto",
              borderRadius: "6px",
              border: `1px solid ${colorStyling.white.border}`,
            }}
          >
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                flexDirection="column"
                alignItems="center"
                sx={{ py: 4 }}
              >
                <CircularProgress
                  size={20}
                  thickness={3}
                  sx={{ color: colorStyling.primary }}
                />
                <TypographyNormal
                  sx={{
                    color: colorStyling.primary,
                    marginTop: "15px",
                    fontWeight: 300,
                  }}
                >
                  {tCommon("loadingDataList")}
                </TypographyNormal>
              </Box>
            ) : (
              <>
                {group && group?.schedulesCount >= 0 && (
                  <>
                    {listSchedule.length === 0 ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        flexDirection="column"
                        alignItems="center"
                        sx={{ py: 4 }}
                      >
                        <TypographyNormal>
                          {t("scheduleList.noData")}
                        </TypographyNormal>
                      </Box>
                    ) : (
                      <StyledBox display={"flex"} flexDirection={"column"}>
                        {listSchedule.map((item) => (
                          <Box
                            className={classes.hoverPaper}
                            key={item.id}
                            sx={{
                              p: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <AssignedComponentItem
                              type={"schedule"}
                              item={item}
                              rawFlag={true}
                              sourceData={{
                                ...group,
                                type: "group",
                                editPermission: schedulePermission?.update,
                              }}
                            />
                          </Box>
                        ))}
                      </StyledBox>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" fullWidth onClick={handleClose}>
            {tCommon("closeBtn")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ScheduleListDialog;
